import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, MessageCircleIcon, ShieldCheckIcon } from 'lucide-react';
import { Form, FormAnswer } from '../../types';
import { postSubmitConfig, whatsappLink } from '../../utils/postSubmit';
import { AnswerMap, visibleQuestions } from '../../utils/conditional';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { QuestionField } from './QuestionField';
export interface DynamicFormResult {
  answers: FormAnswer[];
  contact: Record<string, string>;
}
interface DynamicFormProps {
  form: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  /** En vista previa el formulario no envía datos. */
  preview?: boolean;
  /** Permite ocultar la barra de progreso desde el bloque de la landing. */
  showProgress?: boolean;
  /** Permite ocultar el aviso de privacidad desde el bloque de la landing. */
  hidePrivacyNotice?: boolean;
  className?: string;
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Prefijos internacionales para el campo de teléfono. */
const COUNTRY_CODES = [{
  value: '+34',
  label: '🇪🇸 España +34'
}, {
  value: '+52',
  label: '🇲🇽 México +52'
}, {
  value: '+54',
  label: '🇦🇷 Argentina +54'
}, {
  value: '+56',
  label: '🇨🇱 Chile +56'
}, {
  value: '+57',
  label: '🇨🇴 Colombia +57'
}, {
  value: '+51',
  label: '🇵🇪 Perú +51'
}, {
  value: '+1',
  label: '🇺🇸 EE. UU. +1'
}, {
  value: '+351',
  label: '🇵🇹 Portugal +351'
}, {
  value: '+33',
  label: '🇫🇷 Francia +33'
}, {
  value: '+39',
  label: '🇮🇹 Italia +39'
}, {
  value: '+44',
  label: '🇬🇧 Reino Unido +44'
}, {
  value: '+49',
  label: '🇩🇪 Alemania +49'
}];

/**
 * Formulario dinámico paso a paso.
 * Las preguntas NO están escritas dentro del componente: llegan siempre
 * desde la configuración del formulario (`form.questions`).
 */
export function DynamicForm({
  form,
  onSubmit,
  preview = false,
  showProgress = true,
  hidePrivacyNotice = false,
  className = ''
}: DynamicFormProps) {
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [contact, setContact] = useState<Record<string, string>>({});
  const [dialCode, setDialCode] = useState('+34');
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewDone, setPreviewDone] = useState(false);
  const [done, setDone] = useState<DynamicFormResult | null>(null);
  /** Temporizador del avance automático, para no encadenar dos saltos. */
  const advanceTimer = useRef<number | null>(null);
  const questions = useMemo(() => visibleQuestions(form.questions, answers), [form.questions, answers]);

  // El bloque de contacto puede ir al principio (recomendado) o al final.
  const contactFirst = form.contactPosition !== 'final';
  // Un formulario sin datos de contacto no debe mostrar un paso vacío.
  const hasContact = form.contactFields.length > 0;
  const totalSteps = questions.length + (hasContact ? 1 : 0);
  const contactStepIndex = hasContact ? contactFirst ? 0 : questions.length : -1;
  const isContactStep = step === contactStepIndex;
  const current = hasContact && contactFirst ? questions[step - 1] : questions[step];
  const progress = totalSteps > 0 ? Math.round((step + 1) / totalSteps * 100) : 0;

  /**
   * Cada visita empieza SIEMPRE en el primer paso. Este formulario no guarda
   * el progreso del visitante salvo que se active expresamente
   * `saveProgress` en el editor (desactivado por defecto), así que aquí solo
   * se limpia cualquier resto de una visita anterior.
   */
  useEffect(() => {
    if (form.saveProgress) return;
    try {
      window.sessionStorage.removeItem(`ava_form_progress:${form.id}`);
    } catch {
      /* Modo privado de Safari: sin almacenamiento, nada que limpiar. */
    }
  }, [form.id, form.saveProgress]);

  /**
   * Si la lógica condicional oculta preguntas, el paso actual puede quedar
   * fuera de rango; se ajusta en lugar de mostrar una pantalla vacía o
   * descuadrar el contador «Paso X de Y».
   */
  useEffect(() => {
    setStep(s => Math.min(Math.max(0, s), Math.max(0, totalSteps - 1)));
  }, [totalSteps]);

  /** Cancela un avance automático pendiente. */
  function cancelAutoAdvance() {
    if (advanceTimer.current !== null) {
      window.clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  }
  useEffect(() => cancelAutoAdvance, []);
  function setAnswer(id: string, value: string | string[]) {
    setAnswers(prev => ({
      ...prev,
      [id]: value
    }));
    setErrors(prev => ({
      ...prev,
      [id]: ''
    }));
  }
  function validateQuestion(): boolean {
    if (!current || !current.required) return true;
    const value = answers[current.id];
    const empty = value === undefined || (Array.isArray(value) ? value.length === 0 : String(value).trim() === '');
    if (empty) {
      setErrors(prev => ({
        ...prev,
        [current.id]: 'Esta pregunta es obligatoria.'
      }));
      return false;
    }
    if (current.type === 'email' && !EMAIL_RE.test(String(value))) {
      setErrors(prev => ({
        ...prev,
        [current.id]: 'Introduce un email válido.'
      }));
      return false;
    }
    return true;
  }
  function validateContact(): boolean {
    const next: Record<string, string> = {};
    form.contactFields.forEach(field => {
      const value = (contact[field.key] || '').trim();
      if (field.required && !value) next[field.key] = 'Campo obligatorio.';else if (field.type === 'email' && value && !EMAIL_RE.test(value)) next[field.key] = 'Introduce un email válido.';else if (field.type === 'telefono' && value && value.replace(/\D/g, '').length < 6) next[field.key] = 'Introduce un teléfono válido.';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function goNext() {
    if (isContactStep ? !validateContact() : !validateQuestion()) return;
    // Un avance manual anula el automático: si no, se saltaba un paso.
    cancelAutoAdvance();
    setDirection(1);
    setStep(s => Math.min(s + 1, totalSteps - 1));
  }
  function goBack() {
    cancelAutoAdvance();
    setDirection(-1);
    setStep(s => Math.max(0, s - 1));
  }

  /** Avance con Enter en preguntas de una sola línea. */
  function handleKeyDown(event: React.KeyboardEvent<HTMLFormElement>) {
    const target = event.target as HTMLElement;
    if (event.key !== 'Enter' || target.tagName === 'TEXTAREA') return;
    if (!isLastStep) {
      event.preventDefault();
      goNext();
    }
  }
  const isLastStep = step === totalSteps - 1;

  /** Aviso de privacidad editable y ocultable desde el editor. */
  const notice = form.privacyNotice ?? {
    enabled: true,
    text: 'Al enviar este formulario aceptas que utilicemos tus datos para responder a tu solicitud.'
  };

  /** Qué ocurre cuando el visitante termina el formulario. */
  const action = postSubmitConfig(form);
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;
    if (isContactStep && !validateContact()) return;
    if (!isContactStep && !validateQuestion()) return;
    if (preview || !onSubmit) {
      // En vista previa no se crea ningún lead, pero se confirma que la
      // validación ha pasado y el formulario está listo para publicarse.
      setPreviewDone(true);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: FormAnswer[] = questions.map(question => ({
        questionId: question.id,
        label: question.label,
        value: answers[question.id] ?? ''
      }));
      const phoneKey = form.contactFields.find(f => f.type === 'telefono')?.key;
      const contactData = phoneKey ? {
        ...contact,
        [phoneKey]: `${dialCode} ${contact[phoneKey] ?? ''}`.trim()
      } : contact;
      const result: DynamicFormResult = {
        answers: payload,
        contact: contactData
      };
      await onSubmit(result);
      // El lead ya está guardado. Si la acción posterior se resuelve en la
      // propia página (mensaje o WhatsApp), se muestra aquí; si la acción es
      // ir a la página de gracias o a una URL, el contenedor ya ha navegado.
      cancelAutoAdvance();
      setDone(result);
      if (action.action === 'whatsapp' && action.whatsappAuto) {
        const url = whatsappLink(action, result.contact, result.answers);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      // Se muestra la causa real para poder diagnosticar el fallo.
      setSubmitError(err instanceof Error && err.message ? err.message : 'No hemos podido enviar tu solicitud. Inténtalo de nuevo en unos segundos.');
      setSubmitting(false);
    }
  }
  /** Pantalla final: mensaje simple o WhatsApp, sin página de gracias. */
  if (done && (action.action === 'mensaje' || action.action === 'whatsapp')) {
    const url = whatsappLink(action, done.contact, done.answers);
    return <div className={`mx-auto w-full max-w-[820px] rounded-2xl border border-border bg-background px-6 py-12 text-center shadow-soft sm:rounded-3xl sm:px-10 sm:py-16 ${className}`} role="status" data-id="element-408" data-mp="3grmawetp22vr">
        <CheckCircle2Icon className="mx-auto h-12 w-12 text-green-600" aria-hidden="true" data-id="element-409" data-mp="37qxx9l87q4je" />
        <h3 className="mt-5 text-[22px] font-semibold tracking-tight text-navy sm:text-[28px]" data-id="element-410" data-mp="32wbl3fub47y9">
          {action.messageTitle || form.successMessage}
        </h3>
        <p className="mx-auto mt-3 max-w-lg text-sm text-muted-foreground sm:text-base" data-id="element-411" data-mp="2t1jityps4wx5">
          {action.messageText}
        </p>
        {action.action === 'whatsapp' && url && <a href={url} target="_blank" rel="noopener noreferrer" className="ava-glow mt-8 inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-primary px-7 text-base font-medium text-white transition-colors hover:bg-primary/90" data-id="element-412" data-mp="2jz0ltv8odeit">
            <MessageCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-413" data-mp="15n0wz9nq7s5k" />
            {action.whatsappLabel || 'Hablar por WhatsApp'}
          </a>}
      </div>;
  }
  if (form.questions.length === 0 && form.contactFields.length === 0) {
    return <div className="rounded-2xl border border-dashed border-border bg-surface p-10 text-center text-muted-foreground" data-id="element-414" data-mp="rn9jda14gds3">
        Este formulario todavía no tiene preguntas.
      </div>;
  }
  return <div className={`mx-auto w-full max-w-[820px] overflow-hidden rounded-2xl border border-border bg-background shadow-soft sm:rounded-3xl ${className}`} data-id="element-415" data-mp="10ny0idltau97">
      <div className="px-5 py-7 sm:px-10 sm:py-10" data-id="element-416" data-mp="wl1fkg8sum23">
        {/* Progreso */}
        <div className={showProgress ? 'mb-8 sm:mb-10' : 'hidden'} data-id="element-417" data-mp="2c2bgyxe8k7f7">
          <div className="mb-2.5 flex items-center justify-between text-sm" data-id="element-418" data-mp="2821c5o26jn1x">
            <span className="font-medium text-muted-foreground" data-id="element-419" data-mp="2844082jrv640">
              Paso {step + 1} de {totalSteps}
            </span>
            <span className="tabular-nums text-muted-foreground" data-id="element-420" data-mp="2kkwsxa3u27rv">{progress}%</span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Progreso del formulario" data-id="element-421" data-mp="25nmf3u3pwpsz">
            <motion.div className="h-full rounded-full bg-primary" initial={false} animate={{
            width: `${Math.max(progress, 4)}%`
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} data-id="element-422" data-mp="2yb8lfa4by8jj" />
          </div>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} noValidate data-id="element-423" data-mp="2ok4ft10jo01d">
          <AnimatePresence mode="wait" initial={false} data-id="element-424" data-mp="2tj8q5uz7g3ta">
            <motion.div key={isContactStep ? 'contacto' : current?.id} initial={{
            opacity: 0,
            x: direction * 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction * -20
          }} transition={{
            duration: 0.22,
            ease: 'easeOut'
          }} data-id="element-425" data-mp="3hae4g5phyo1h">
              {isContactStep ? <div className="space-y-6" data-id="element-426" data-mp="3mw56y97q9tys">
                  <div className="space-y-2" data-id="element-427" data-mp="3mv3mje4tnv4e">
                    <h3 className="text-[22px] font-semibold leading-tight tracking-tight text-navy sm:text-[28px]" data-id="element-428" data-mp="3hxtvju1sbbeg">
                      {form.contactTitle || 'Empecemos por tu información de contacto'}
                    </h3>
                    <p className="text-sm text-muted-foreground sm:text-base" data-id="element-429" data-mp="386pgkuc3zqjm">
                      Solo te llevará un minuto.
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2" data-id="element-430" data-mp="2z7ly9skrnf66">
                    {form.contactFields.map(field => field.type === 'telefono' ? <div key={field.key} className="sm:col-span-2" data-id="element-431" data-mp="2u6y10p5y223i">
                          <div className="grid grid-cols-[104px_minmax(0,1fr)] gap-2.5 sm:grid-cols-[minmax(0,150px)_minmax(0,1fr)] sm:gap-3" data-id="element-432" data-mp="2p8cy2hzb54bd">
                            <Select id="contact-dial" label="País" options={COUNTRY_CODES} value={dialCode} onChange={e => setDialCode(e.target.value)} data-id="element-433" data-mp="14qc96p5w47l6" />
                            <Input id={`contact-${field.key}`} label={`${field.label}${field.required ? ' *' : ''}`} type="tel" inputMode="tel" placeholder={field.placeholder} value={contact[field.key] || ''} error={errors[field.key]} onChange={e => setContact(prev => ({
                      ...prev,
                      [field.key]: e.target.value
                    }))} data-id="element-434" data-mp="1eoi7yxwegxlt" />
                          </div>
                        </div> : <div key={field.key} className={field.key === 'email' || field.key === 'website' || field.key === 'company' ? 'sm:col-span-2' : ''} data-id="element-435" data-mp="uzzngtmca8dg">
                          <Input id={`contact-${field.key}`} label={`${field.label}${field.required ? ' *' : ''}`} type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'} inputMode={field.type === 'email' ? 'email' : undefined} placeholder={field.placeholder} value={contact[field.key] || ''} error={errors[field.key]} onChange={e => setContact(prev => ({
                    ...prev,
                    [field.key]: e.target.value
                  }))} data-id="element-436" data-mp="39uvo9w92j27" />
                        </div>)}
                  </div>
                </div> : current ? <QuestionField question={current} value={answers[current.id]} error={errors[current.id]} onChange={value => {
              setAnswer(current.id, value);
              // Avance automático en selección única. Se cancela el anterior y
              // solo salta si el visitante sigue en este mismo paso, para que
              // un doble toque en el móvil no se salte una pregunta.
              if (current.type === 'seleccion_unica' && !isLastStep) {
                cancelAutoAdvance();
                const from = step;
                advanceTimer.current = window.setTimeout(() => {
                  advanceTimer.current = null;
                  setDirection(1);
                  setStep(s => s === from ? Math.min(s + 1, totalSteps - 1) : s);
                }, 260);
              }
            }} data-id="element-437" data-mp="3pak3kpovcbf4" /> : null}
            </motion.div>
          </AnimatePresence>

          {submitError && <p role="alert" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-id="element-438" data-mp="jf5hjd875u8a">
              {submitError}
            </p>}

          {previewDone && <p role="status" className="mt-6 rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-700" data-id="element-439" data-mp="aeqsc2s43uzi">
              Vista previa: el formulario es válido. En la página publicada este
              envío crearía un contacto en tu CRM.
            </p>}

          <div className="mt-9 flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-440" data-mp="jfcti8vqjqp">
            {/* En el primer paso no se muestra «Anterior». */}
            {step > 0 ? <Button type="button" variant="ghost" onClick={goBack} disabled={submitting} className="min-h-[48px] w-full sm:w-auto" leftIcon={<ArrowLeftIcon className="h-4 w-4" data-id="element-442" data-mp="1ydb3305fsezr" />} data-id="element-441" data-mp="1zu6nufd45i6x">
                Anterior
              </Button> : <span aria-hidden="true" className="hidden sm:block" data-id="element-443" data-mp="1tdex4vd6idhk" />}
            {isLastStep ? <Button type="submit" size="lg" loading={submitting} disabled={submitting} className="ava-glow min-h-[52px] w-full sm:w-auto" data-id="element-444" data-mp="1oevd50i5bbru">
                {preview ? 'Vista previa' : form.submitLabel}
              </Button> : <Button type="button" size="lg" onClick={goNext} className="min-h-[52px] w-full sm:w-auto" rightIcon={<ArrowRightIcon className="h-4 w-4" data-id="element-446" data-mp="2c3wds1kqlimk" />} data-id="element-445" data-mp="2h429bsu4np9x">
                Siguiente
              </Button>}
          </div>
        </form>

        {/* Aviso informativo: nunca bloquea el envío. */}
        {notice.enabled && !hidePrivacyNotice && <p className="mt-6 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-xs leading-relaxed text-muted-foreground" data-id="element-447" data-mp="22duwptqsx0h5">
            <ShieldCheckIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-448" data-mp="2v3k2ekeghop3" />
            <span data-id="element-449" data-mp="2v1h1v6fogd64">{notice.text}</span>
            {notice.linkUrl && <a href={notice.linkUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline-offset-2 hover:underline" data-id="element-450" data-mp="33czhognceajj">
                {notice.linkLabel || 'Política de privacidad'}
              </a>}
          </p>}
      </div>
    </div>;
}